// src/plugins/sales-forecast/components/NeighborhoodCompositionControl.tsx
import React from "react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../../components/ui/card";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface NeighborhoodCompositionControlProps {
  initialSpecs: number;
  additionalSpecs?: number;
  preSolds: number;
  availableLots: number;
  specsToStart: number;
  perSpecsSold: number;

  onSpecsChange: (v: number) => void;
  onPreSoldsChange: (v: number) => void;
  onAvailableLotsChange: (v: number) => void;
  onSpecsToStartChange: (v: number) => void;
  onPerSpecsSoldChange: (v: number) => void;

  min?: number;
  max?: number;
  step?: number;
}

export const NeighborhoodCompositionControl: React.FC<
  NeighborhoodCompositionControlProps
> = ({
  initialSpecs,
  preSolds,
  availableLots,
  specsToStart,
  perSpecsSold,
  onSpecsChange,
  onPreSoldsChange,
  onAvailableLotsChange,
  onSpecsToStartChange,
  onPerSpecsSoldChange,
  min = 0,
  max = 100000,
  step = 1,
}) => {
  const theme = useSystemTheme();

  // Grab live theme CSS variables
  const cardBg =
    "var(--subcard-bg";
  const textColor = "var(--text-color)";
  const borderColor = "var(--border-color)";

  // Use the darker input background to match Property Features
  const inputBg =
    "var(--input-bg)"; // fallback darker tone

  const inputClass =
    "w-full text-center rounded shadow-sm focus:outline-none transition-all duration-150 [appearance:auto]";
  const inputStyle = {
    backgroundColor: inputBg, // darker tone than card
    color: textColor,
    border: `1px solid ${borderColor}`,
    boxShadow: "inset 0 0 3px rgba(0, 0, 0, 0.3)", // subtle inset depth
  };

  const labelClass = "text-sm font-medium mb-1";
  const labelStyle = { color: textColor };

  return (
    <Card
      className={`theme-${theme} shadow-md rounded-xl transition-all duration-200`}
      style={{
        backgroundColor: cardBg,
        color: textColor,
        border: `1px solid ${borderColor}`,
      }}
    >
      <CardHeader>
        <CardTitle style={{ color: textColor }}>
          Neighborhood Composition
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Total Available Lots */}
        <div className="flex flex-col">
          <Label
            htmlFor="availableLots"
            className={labelClass}
            style={labelStyle}
          >
            Total Lots
          </Label>
          <Input
            id="availableLots"
            type="number"
            value={availableLots}
            onChange={(e) => onAvailableLotsChange(Number(e.target.value))}
            className={inputClass}
            style={inputStyle}
            min={min}
            max={max}
            step={step}
          />
        </div>

        {/* Initial Specs */}
        <div className="flex flex-col">
          <Label
            htmlFor="initialSpecs"
            className={labelClass}
            style={labelStyle}
          >
            Initial Specs
          </Label>
          <Input
            id="initialSpecs"
            type="number"
            value={initialSpecs}
            onChange={(e) => onSpecsChange(Number(e.target.value))}
            className={inputClass}
            style={inputStyle}
            min={min}
            max={max}
            step={step}
          />
        </div>

        {/* Pre-Solds */}
        <div className="flex flex-col">
          <Label
            htmlFor="initialPreSolds"
            className={labelClass}
            style={labelStyle}
          >
            Initial Pre-Solds
          </Label>
          <Input
            id="initialPreSolds"
            type="number"
            value={preSolds}
            onChange={(e) => onPreSoldsChange(Number(e.target.value))}
            className={inputClass}
            style={inputStyle}
            min={min}
            max={max}
            step={step}
          />
        </div>

        {/* Additional Specs */}
        <div className="flex flex-col w-full">
          <Label htmlFor="specRatio" className={labelClass} style={labelStyle}>
            Additional Specs
          </Label>
          <div className="grid grid-cols-[auto_1fr_auto_1fr_auto] items-center gap-2 w-full">
            <span className="text-sm" style={{ color: textColor }}>
              Start
            </span>
            <Input
              id="specsToStart"
              type="number"
              value={specsToStart}
              onChange={(e) => onSpecsToStartChange(Number(e.target.value))}
              className={inputClass}
              style={inputStyle}
              min={0}
            />
            <span className="text-sm" style={{ color: textColor }}>
              per
            </span>
            <Input
              id="specsSold"
              type="number"
              value={perSpecsSold}
              onChange={(e) => onPerSpecsSoldChange(Number(e.target.value))}
              className={inputClass}
              style={inputStyle}
              min={1}
            />
            <span className="text-sm" style={{ color: textColor }}>
              Sold
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};